import React, { createContext, useCallback, useMemo, useContext, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import { hasPermission, getUserPermissions } from '../lib/permissions';
import { supabase } from '../lib/supabase';

interface PermissionsContextType {
  userPermissions: string[];
  loading: boolean;
  checkPermission: (permissionId: string) => boolean;
  refreshPermissions: () => Promise<void>;
  userRoleId: number | null;
}

const PermissionsContext = createContext<PermissionsContextType | undefined>(undefined);

export const PermissionsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [userPermissions, setUserPermissions] = useState<string[]>([]);
  const [userProfile, setUserProfile] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const fetchUserProfile = async () => {
    if (!user?.id) return null;
    
    try {
      const { data, error } = await supabase
        .from('user_profile')
        .select('*')
        .eq('auth_id', user.id)
        .single();
      
      if (error) {
        console.error('Erro ao buscar perfil do usuário:', error);
        return null;
      }
      
      return data;
    } catch (err) {
      console.error('Erro ao buscar perfil do usuário:', err);
      return null;
    }
  };

  const loadPermissions = async () => {
    if (!user) {
      setUserPermissions([]);
      return;
    }

    try {
      setLoading(true);
      
      // Buscar o perfil do usuário
      const profile = await fetchUserProfile();
      if (!profile) {
        console.error('Não foi possível carregar o perfil do usuário');
        setUserPermissions([]);
        return;
      }
      
      setUserProfile(profile);
      
      // Buscar as permissões do usuário baseado no perfil
      const permissions = await getUserPermissions(profile.id);
      setUserPermissions(permissions);
    } catch (err) {
      console.error('Erro ao carregar permissões:', err);
      setUserPermissions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPermissions();
    // Depende de `user?.id`, não do objeto `user` (ver App.tsx) — evita recarregar
    // as permissões toda vez que a aba volta a ficar visível.
  }, [user?.id]);

  const checkPermission = useCallback(
    (permissionId: string): boolean => userPermissions.includes(permissionId),
    [userPermissions]
  );

  const refreshPermissions = useCallback(async () => {
    await loadPermissions();
    // loadPermissions só depende de quem está logado
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const userRoleId = userProfile?.user_role_id || null;
  const value = useMemo(
    () => ({ userPermissions, loading, checkPermission, refreshPermissions, userRoleId }),
    [userPermissions, loading, checkPermission, refreshPermissions, userRoleId]
  );

  return (
    <PermissionsContext.Provider
      value={value}
    >
      {children}
    </PermissionsContext.Provider>
  );
};

export const usePermissions = () => {
  const context = useContext(PermissionsContext);
  if (context === undefined) {
    throw new Error('usePermissions must be used within a PermissionsProvider');
  }
  return context;
};

// Hook para simplificar a verificação de permissões em componentes
export const useHasPermission = (permissionId: string): boolean => {
  const { checkPermission, loading } = usePermissions();
  
  // Se estiver carregando, não conceder a permissão por segurança
  if (loading) return false;
  
  return checkPermission(permissionId);
}; 